import { useEffect, useRef } from "react";
import { IoClose } from "react-icons/io5";
import { useNativeBackPress } from "../../hooks/useNativebackPress";
import { ROUTES } from "../../routes/routePaths";
import "./Modal.scss";

export default function Modal({
  isOpen = false,
  onClose,
  title = "",
  children,
  showCloseButton = true,
  className = "",
  closeOnBackdropClick = true,
  isBottomSheet = false,
  enableSwipeToClose = isBottomSheet,
  isPermissionModal = false,
}) {
  const modalRef = useRef(null);
  const bodyRef = useRef(null);
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);
  const isDragging = useRef(false);
  const dragStartedAtTop = useRef(false);

  const resetPosition = () => {
    if (modalRef.current) {
      modalRef.current.style.transform = "";
      modalRef.current.style.transition = "";
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      resetPosition();
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useNativeBackPress({
    respond: isOpen,
    mode: isPermissionModal ? "exit" : "back",
    route: !isPermissionModal ? ROUTES.HOME : undefined,
  });

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTouchStart = (e) => {
    if (!enableSwipeToClose) return;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    touchStartY.current = clientY;
    touchCurrentY.current = clientY;
    isDragging.current = true;

    const bodyScrollTop = bodyRef.current ? bodyRef.current.scrollTop : 0;
    dragStartedAtTop.current = bodyScrollTop <= 0;

    if (modalRef.current) {
      modalRef.current.style.transition = "none";
    }
  };

  const handleTouchMove = (e) => {
    if (!enableSwipeToClose || !isDragging.current) return;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    touchCurrentY.current = clientY;
    const deltaY = clientY - touchStartY.current;

    const bodyScrollTop = bodyRef.current ? bodyRef.current.scrollTop : 0;

    if (bodyScrollTop <= 0 && !dragStartedAtTop.current) {
      dragStartedAtTop.current = true;
      touchStartY.current = clientY;
      return;
    }

    if (dragStartedAtTop.current && deltaY > 0) {
      if (modalRef.current) {
        modalRef.current.style.transform = `translateY(${deltaY}px)`;
      }
    }
  };

  const handleTouchEnd = () => {
    if (!enableSwipeToClose || !isDragging.current) return;
    isDragging.current = false;

    const deltaY = touchCurrentY.current - touchStartY.current;

    if (dragStartedAtTop.current && deltaY > 80) {
      if (modalRef.current) {
        modalRef.current.style.transition = "transform 0.2s ease-out";
        modalRef.current.style.transform = "translateY(100%)";
      }
      setTimeout(() => {
        onClose();
        resetPosition();
      }, 200);
    } else {
      if (modalRef.current) {
        modalRef.current.style.transition = "transform 0.2s ease-out";
        modalRef.current.style.transform = "translateY(0px)";
      }
      setTimeout(() => {
        resetPosition();
      }, 200);
    }
  };

  return (
    <div
      className={`modal-overlay ${isBottomSheet ? "modal-overlay--bottom-sheet" : ""} ${isPermissionModal ? "modal-overlay--white" : ""}`}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className={`modal-container ${isBottomSheet ? "bottom-sheet-modal" : ""} ${className}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleTouchStart}
        onMouseMove={handleTouchMove}
        onMouseUp={handleTouchEnd}
        onMouseLeave={handleTouchEnd}
      >
        {(title || showCloseButton) && (
          <div className={`modal-header ${!title ? "modal-header--close-only" : ""}`}>
            {title && <h2 className="modal-title">{title}</h2>}
            {showCloseButton && (
              <button
                type="button"
                className="modal-close-btn"
                onClick={onClose}
                aria-label="Close modal"
              >
                <IoClose />
              </button>
            )}
          </div>
        )}
        <div ref={bodyRef} className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}
